import { DateTime } from 'luxon';

const MILLIS_IN_DAY = 86400000;
const MILLIS_IN_HOUR = 3600000;
const MILLIS_IN_MINUTE = 60000;
const MILLIS_IN_SECOND = 1000;

interface DateTimeFormatterOptions {
  date: number;
  excludeRelativeDay?: boolean;
  includeTime?: boolean;
  showFullMonthName?: boolean;
}

interface TimeFormatterOptions {
  date: DateTime;
  excludeRelativeDay?: boolean;
  includeTime?: boolean;
  showFullMonthName?: boolean;
}

class TimeFormatter {
  epochToHumanReadableTime(
    epochTime: number,
    removeSpace: boolean = false
  ): string {
    if (!epochTime) {
      return null;
    }
    const time = DateTime.fromMillis(epochTime);
    const format = removeSpace ? 'h:mma' : 'h:mm a';
    return time.toFormat(format);
  }

  getRoughDurationString(duration: number): string {
    if (duration === 0) {
      return `0s`;
    }

    if (!duration) {
      return null;
    }

    const humanizedMap = {
      days: undefined,
      hours: undefined,
      minutes: undefined,
      seconds: undefined,
    };

    let durationRemainder = Math.abs(duration);

    if (duration > MILLIS_IN_DAY) {
      const numDays = Math.trunc(durationRemainder / MILLIS_IN_DAY);
      humanizedMap['days'] = numDays;
      durationRemainder -= numDays * MILLIS_IN_DAY;
    }

    if (duration > MILLIS_IN_HOUR) {
      const numHours = Math.trunc(durationRemainder / MILLIS_IN_HOUR);
      humanizedMap['hours'] = numHours;
      durationRemainder -= numHours * MILLIS_IN_HOUR;
    }

    if (duration > MILLIS_IN_MINUTE) {
      const numMinutes = Math.trunc(durationRemainder / MILLIS_IN_MINUTE);
      humanizedMap['minutes'] = numMinutes;
      durationRemainder -= numMinutes * MILLIS_IN_MINUTE;
    }

    if (durationRemainder !== 0) {
      const numSeconds = durationRemainder / MILLIS_IN_SECOND;
      humanizedMap['seconds'] = Math.round(numSeconds);
    }

    const { days, hours, minutes, seconds } = humanizedMap;
    const humanizedNumberStrings = [];

    if (days) {
      humanizedNumberStrings.push(`${days}d`);
    }
    if (hours) {
      humanizedNumberStrings.push(`${hours}h`);
    }
    if (minutes) {
      humanizedNumberStrings.push(`${minutes}m`);
    }
    if (seconds) {
      humanizedNumberStrings.push(`${seconds}s`);
    }

    return `${duration < 0 ? '-' : ''}${humanizedNumberStrings.join(' ')}`;
  }

  dateIsPast(dateInMillis: number): boolean {
    const date = DateTime.fromMillis(dateInMillis);
    const now = DateTime.now();
    return date.startOf('day') <= now.startOf('day');
  }

  getDateStringFromString(dateString: string): string {
    const date = DateTime.fromISO(dateString);
    return this.getDateString({ date });
  }

  epochToShortMonthDay(datetime: number): string {
    const dateTimeObj = DateTime.fromMillis(datetime);
    const shouldShowYear = dateTimeObj.year !== DateTime.now().year;
    const format = shouldShowYear ? 'MMM d, yyyy' : 'MMM d';
    return dateTimeObj.toFormat(format);
  }

  epochToHumanReadableDate({
    date,
    includeTime,
    excludeRelativeDay,
    showFullMonthName,
  }: DateTimeFormatterOptions): string {
    if (!date) {
      return null;
    }
    const time = DateTime.fromMillis(Math.round(date));

    return this.getDateString({
      date: time,
      includeTime,
      excludeRelativeDay,
      showFullMonthName,
    });
  }

  epochToShortDate(epochTime: number, useZeroPadding?: boolean): string {
    if (!epochTime) {
      return null;
    }
    const date = DateTime.fromMillis(Math.round(epochTime));
    const monthFormat = useZeroPadding ? 'LL' : 'L';

    return date.toFormat(`${monthFormat}/dd/yyyy`);
  }

  getDateString({
    date,
    includeTime,
    excludeRelativeDay,
    showFullMonthName,
  }: TimeFormatterOptions): string {
    const relativeDateString = excludeRelativeDay
      ? null
      : date.toRelativeCalendar({
          unit: 'days',
        });
    const dateString = [];
    switch (relativeDateString) {
      case 'today':
        dateString.push('Today');
        break;
      case 'tomorrow':
        dateString.push('Tomorrow');
        break;
      case 'yesterday':
        dateString.push('Yesterday');
        break;
      default:
        dateString.push(
          date.toFormat(showFullMonthName ? 'MMMM dd, yyyy' : 'MMM dd, yyyy')
        );
        break;
    }
    if (includeTime) {
      dateString.push(date.toFormat('• h:mma').toLowerCase());
    }
    return dateString.join(' ');
  }

  // yyyy-MM-dd
  formatDateOfBirthString(
    dateOfBirth: string,
    includeAge: boolean = true
  ): string {
    const dobDateTime = DateTime.fromFormat(dateOfBirth, 'yyyy-MM-dd');
    const formattedDob = dobDateTime.toLocaleString(DateTime.DATE_MED);
    if (!includeAge) return formattedDob;
    const age = dobDateTime
      ? Math.floor(DateTime.now().diff(dobDateTime, 'years').years)
      : 'Unknown';
    return `${formattedDob} (${age} years old)`;
  }

  durationDateString(start: number, end: number) {
    const startDt = DateTime.fromMillis(start);
    const endDt = DateTime.fromMillis(end);
    const sameDay = startDt.hasSame(endDt, 'day');
    if (sameDay) {
      return `${this.getDateString({
        date: startDt,
        includeTime: true,
      })} - ${endDt
        .toFormat('h:mma')
        .toLowerCase()} (${this.getRoughDurationString(end - start)})`;
    }

    return `${this.getDateString({
      date: startDt,
      includeTime: true,
    })} -
    ${this.getDateString({
      date: endDt,
      includeTime: true,
    })} (${this.getRoughDurationString(end - start)})`;
  }

  millisToDurationString(duration: number) {
    const hours = Math.floor(duration / 3600000);
    const minutes = Math.floor((duration % 3600000) / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);

    const outputHours = (minutes === 60 ? `${hours + 1}` : `${hours}`).padStart(
      2,
      '0'
    );
    const outputMinutes = (
      seconds === 60 ? `${minutes + 1}` : `${minutes}`
    ).padStart(2, '0');
    const outputSeconds = (seconds === 60 ? '00' : `${seconds}`).padStart(
      2,
      '0'
    );

    return `${
      outputHours === '00' ? '' : outputHours + ':'
    }${outputMinutes}:${outputSeconds}`;
  }

  differenceInDaysFromNow(futureTime: number, currentTime?: number): number {
    const futureEndOfDay = DateTime.fromMillis(futureTime).endOf('day');
    const currentEndOfDay = (
      currentTime ? DateTime.fromMillis(currentTime) : DateTime.now()
    ).endOf('day');

    return Math.floor(futureEndOfDay.diff(currentEndOfDay, 'days').days);
  }
}

export const timeFormatter = new TimeFormatter();
